import { Card } from "primereact/card";
import Table from "./table";
import type { ColumnMeta } from "../constants/table.cnst";
import { fetchApi } from "../services/http.service";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { IconArrowNarrowRight } from "@tabler/icons-react";
import { Dialog } from "primereact/dialog";
import { REPORT_TYPES } from "../constants/data.cnst";

interface CardTableProps {
    config: {
        header: string;
        subtitle: string;
        icon: React.ElementType;
        api: string;
        columns: ColumnMeta[];
        viewAllText?: string;
    };
    filters: any;
}

function CardTable({
    config,
    filters,
}: CardTableProps) {
    const {
        header,
        subtitle,
        icon: Icon,
        api,
        columns,
        viewAllText,
    } = config;

    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(10);
    const [tableData, setTableData] = useState<any[]>([]);
    const [pageNum, setPageNum] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [isDialogVisible, setIsDialogVisible] = useState(false);

    /*
     * Reset table whenever date filters change.
     */
    useEffect(() => {
        setTableData([]);
        setTotalRecords(0);
        setFirst(0);
        setPageNum(1);
        setIsDialogVisible(false);
    }, [filters.fromDate, filters.toDate]);

    const cardHeader = (
        <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-full bg-pink-100 flex items-center justify-center">
                    <Icon
                        size={22}
                        className="text-pink-500"
                    />
                </div>

                <div>
                    <h3>
                        {header}
                    </h3>

                    <p className="font-medium">
                        {subtitle}
                    </p>
                </div>
            </div>
        </div>
    );

    /*
     * API query.
     * API is manually triggered using refetch().
     */
    const {
        data,
        isFetching,
        refetch,
    } = useQuery({
        queryKey: [
            "card-table",
            api,
            filters.fromDate,
            filters.toDate,
            pageNum,
            rows,
        ],

        queryFn: () =>
            fetchApi({
                endpoint: api,
                queryParams: {
                    fromDate: filters.fromDate,
                    toDate: filters.toDate,
                    page_num: pageNum,
                    total_entry_per_page: rows,
                },
                resourceName: "Reports_Events_" + REPORT_TYPES.HEADER.EVENTS.TAG_MISSING_NEW
            }),

        staleTime: 0,
        gcTime: 0,
        enabled: false,
    });

    /*
     * API call on date change.
     *
     * fromDate is mandatory for the API call.
     * toDate is optional.
     */
    useEffect(() => {
        if (filters.fromDate) {
            refetch();
        }
    }, [
        filters.fromDate,
        filters.toDate,
        refetch,
    ]);

    /*
     * Update table data whenever API response changes.
     */
    useEffect(() => {
        if (data) {
            setTableData(
                data.response?.data ?? []
            );

            setTotalRecords(
                data.response?.total_entries ?? 0
            );
        }
    }, [data]);

    /*
     * API call when pagination changes inside the dialog.
     */
    useEffect(() => {
        if (
            isDialogVisible &&
            filters.fromDate
        ) {
            refetch();
        }
    }, [
        pageNum,
        rows,
        filters.fromDate,
        refetch,
    ]);

    /*
     * Reusable table content.
     *
     * Card:
     * - Always shows only first 5 records.
     * - View All button remains visible.
     *
     * Dialog:
     * - Shows complete paginated table.
     */
    function TableContent({
        isDialog = false,
    }: {
        isDialog?: boolean;
    }) {
        return (
            <div className="overflow-hidden">
                <Table
                    columns={columns}
                    data={
                        isDialog
                            ? tableData
                            : tableData.slice(0, 5)
                    }
                    showGridlines
                    paginator={isDialog}
                    isLoading={isFetching}
                    first={isDialog ? first : 0}
                    rows={rows}
                    totalRecords={
                        isDialog
                            ? totalRecords
                            : tableData.length
                    }
                    onPageChangeExternal={(
                        page_num,
                        total_entry_per_page
                    ) => {
                        setFirst(
                            (page_num - 1) *
                            total_entry_per_page
                        );

                        setRows(
                            total_entry_per_page
                        );

                        setPageNum(page_num);
                    }}
                />

                {!isDialog &&
                    tableData.length > 5 && (
                        <Button
                            text
                            className="text-brand-600 dark:text-brand-400 mt-2"
                            onClick={() => {
                                setFirst(0);
                                setRows(10);
                                setPageNum(1);
                                setIsDialogVisible(
                                    true
                                );
                            }}
                        >
                            {viewAllText}
                            <IconArrowNarrowRight
                                size={20}
                            />
                        </Button>
                    )}
            </div>
        );
    }

    return (
        <>
            {/* Main Card */}
            <Card
                className="p-card-body-p-0 h-full overflow-hidden"
                header={cardHeader}
            >
                <TableContent />
            </Card>

            {/* Full Table Dialog */}
            <Dialog
                resizable={false}
                draggable={false}
                visible={isDialogVisible}
                onHide={() =>
                    setIsDialogVisible(false)
                }
                style={{
                    width: "70vw",
                }}
            >
                <div className="p-2 overflow-hidden">
                    <TableContent isDialog />
                </div>
            </Dialog>
        </>
    );
}

export default CardTable;